import { Icon, Menu, MenuItem } from "@material-ui/core";
import {
    BugReport as BugReportIcon,
    Edit as EditIcon,
    LiveHelpOutlined as LiveHelpIcon,
    MenuBookOutlined as MenuBookIcon,
} from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Divider } from "js/Components/Divider";
import { Icon as MaterialSymbolIcon } from "js/Components/Icon";
import { buildVersion, isInternalBAIUser } from "js/config";
import AppController from "js/core/AppController";
import QuickStartController from "js/controllers/QuickStartController";
import { isShift } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FeedbackDialog from "js/react/components/Dialogs/FeedbackDialog";
import { t } from "js/i18n";
import { PanelType } from "js/editor/PresentationEditor/PresentationEditorController";

const Info = styled.div`
    padding: 15px;
    color: rgba(0, 0, 0, 0.4);
    font-size: 10px;
    pointer-events: auto;
    user-select: text;
    max-width: 220px;
`;

const BuildVersion = styled.span`
    word-wrap: break-word;
    word-break: break-all;
    white-space: normal;
`;

const StyledIcon = styled(Icon)`
    &&& {
        font-size: 24px;
        padding: 8px;
        cursor: pointer;

        &:hover {
            background: rgba(255, 255, 255, 0.10);
            border-radius: 2px;
        }
    }
    
`;

const StyledQuickStartMaterialSymbolIcon = styled(MaterialSymbolIcon)`
    &&& {
        margin-right: 8px;
    }
`;

export default function HelpMenu({ showQuickStartGuide = false, useOutlineIcon = false }) {
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const [shiftIsPressed, setShiftIsPressed] = useState(false);

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === "Escape") {
                setHelpAnchorEl(null);
            }
            if (isShift(event.which)) {
                setShiftIsPressed(true);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    const closeMenu = () => {
        setHelpAnchorEl(null);
    };

    const showHelp = event => {
        if (!helpAnchorEl) {
            trackActivity("HelpMenu", "Open", null, null, {}, { audit: false });
        }
        setHelpAnchorEl(event.currentTarget);
    };

    const handleOpenZendesk = () => {
        trackActivity("HelpMenu", "ChatSupport", null, null, {}, { audit: false });
        if (window.zendeskInit) {
            window.zendeskInit();
        }
        closeMenu();
    };

    const handleLinkToHelpCenter = () => {
        trackActivity("HelpMenu", "HelpCenter", null, null, {}, { audit: false });
        window.open("https://support.beautiful.ai", "_blank");
        closeMenu();
    };

    const handleShowFeedbackDialog = () => {
        trackActivity("HelpMenu", "GiveFeedback", null, null, {}, { audit: false });
        ShowDialog(FeedbackDialog);
        closeMenu();
    };

    const handleQuickStartGuide = async () => {
        closeMenu();
        try {
            QuickStartController.setHiddenForNow(false);
            if (QuickStartController.hiddenForNow) {
                QuickStartController.setForceOpen(true);
                return;
            }

            await QuickStartController.resetAllTasks();
            await QuickStartController.reset();
            await QuickStartController.initialize();
            QuickStartController.setForceOpen(true);
            trackActivity("OnboardingChecklist", "Shown", null, null, {
                launched_from: "help_menu"
            });
        } catch (error) {
            ShowErrorDialog({
                title: "Oops, something went wrong",
                message: <>
                    <span>
                        {t("An error occurred while setting up the quick start guide. Please try again. If the problem persists, please contact us at {{email}} so we can help you directly.", {
                            email: "support@beautiful.ai"
                        })}
                    </span>
                </>,
            });
        }
    };

    return (
        <>
            <StyledIcon id="help-icon" onClick={showHelp}>{useOutlineIcon ? "help_outline" : "help"}</StyledIcon>
            <Menu
                open={Boolean(helpAnchorEl)}
                anchorEl={helpAnchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                onClose={closeMenu}
            >
                <MenuItem onClick={handleOpenZendesk}>
                    <LiveHelpIcon />
                    <span>{t("Chat with Support")}</span>
                </MenuItem>
                <MenuItem onClick={handleLinkToHelpCenter}>
                    <MenuBookIcon />
                    <span>{t("Browse Help Center")}</span>
                </MenuItem>
                {showQuickStartGuide && (QuickStartController.areAllTasksDone() || QuickStartController.hiddenForNow) && (
                    <MenuItem onClick={handleQuickStartGuide}>
                        <StyledQuickStartMaterialSymbolIcon color="#666" outlined large>rocket_launch</StyledQuickStartMaterialSymbolIcon>
                        <span>{t("Quick Start Guide")}</span>
                    </MenuItem>
                )}
                <MenuItem onClick={handleShowFeedbackDialog}>
                    <EditIcon />
                    <span>{t("Give Feedback")}</span>
                </MenuItem>
                <MenuItem onClick={() => {
                    trackActivity("HelpMenu", "DebugInfo", null, null, {}, { audit: false });
                    AppController.showDebugInfo();
                    closeMenu();
                }}>
                    <BugReportIcon />
                    <span>{t("Debug Info")}</span>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                    trackActivity("HelpMenu", "KeyboardShortcuts", null, null, {}, { audit: false });
                    AppController.setExternalActivePanel(PanelType.SHORTCUTS);
                    closeMenu();
                }}>
                    <span>{t("Keyboard Shortcuts")}</span>
                    <span style={{ marginLeft: 50 }}>⌘ ?</span>
                </MenuItem>
                {shiftIsPressed && <Divider />}
                {shiftIsPressed && (
                    <Info>
                        <BuildVersion>{t("Build")} {buildVersion}</BuildVersion>
                    </Info>
                )}
                {isInternalBAIUser((AppController.user || {}).getEmail?.() || "") && (
                    <MenuItem onClick={() => {
                        AppController.setExternalActivePanel(null);
                        AppController.showInternalBugReport();
                        closeMenu();
                    }}>
                        <BugReportIcon />
                        <span>{t("Internal Bug Report")}</span>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}
